<template>
  <span v-if="showAction" class="evat-submit-action">
    <evat-status-chip v-if="showStatusChip" :evat="evat"></evat-status-chip>

    <v-btn
      v-if="evat && evat.can_submit"
      small
      text
      color="blue"
      class="evat-submit-action__btn"
      :loading="submitting"
      :disabled="submitting"
      @click="confirmDialog = true"
    >
      {{ submitLabel }}
    </v-btn>

    <v-dialog v-model="confirmDialog" width="450px">
      <v-card>
        <v-card-title class="font-weight-light">Confirm</v-card-title>
        <v-card-text class="text-dark pt-2">
          Once this {{ documentLabel }} has been submitted to GRA, it cannot be
          deleted or edited. Proceed?
        </v-card-text>
        <v-card-actions>
          <v-btn
            :disabled="submitting"
            @click="confirmAndSubmit"
            rounded
            depressed
            :loading="submitting"
            block
            large
            color="blue darken-4"
            :dark="!submitting"
          >
            {{ isFailed ? "Retry submission" : "Send to GRA" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar color="success" v-model="showSuccess">
      <v-icon>mdi-check-circle-outline</v-icon>
      {{ successMessage }}
    </v-snackbar>
  </span>
</template>

<script>
import EvatStatusChip from "./EvatStatusChip.vue";
import { isGhanaBusiness } from "../../utils/evat";

const DOCUMENT_LABELS = {
  invoices: "invoice",
  sales: "sale",
  bills: "bill",
};

export default {
  name: "EvatSubmitAction",
  components: { EvatStatusChip },
  props: {
    documentType: {
      type: String,
      required: true,
      validator: (v) => ["invoices", "sales", "bills"].includes(v),
    },
    documentId: {
      type: [Number, String],
      required: true,
    },
    evat: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      confirmDialog: false,
      submitting: false,
      showSuccess: false,
      successMessage: "",
    };
  },
  computed: {
    isGhana() {
      return isGhanaBusiness(this.$store);
    },
    isFailed() {
      return this.evat?.status === "failed";
    },
    documentLabel() {
      return DOCUMENT_LABELS[this.documentType] || "document";
    },
    isVerifiedPreview() {
      return (
        ["invoices", "sales"].includes(this.documentType) &&
        (this.evat?.status === "submitted" || this.evat?.is_verified)
      );
    },
    showStatusChip() {
      if (!this.isGhana || !this.evat?.status) return false;
      return !this.isVerifiedPreview;
    },
    showAction() {
      if (!this.isGhana) return false;
      return (
        this.showStatusChip ||
        this.evat?.can_submit ||
        this.showSuccess ||
        this.submitting ||
        this.confirmDialog
      );
    },
    submitLabel() {
      return this.isFailed ? "Retry submission" : "Send to GRA";
    },
  },
  methods: {
    async confirmAndSubmit() {
      await this.submit();
      this.confirmDialog = false;
    },
    normalizeSubmitResponse(raw) {
      const payload = raw?.data && typeof raw.data === "object" && !raw.qr_code
        ? raw.data
        : raw || {};
      const nested =
        payload.evat && typeof payload.evat === "object" ? payload.evat : {};

      const status = nested.status || payload.status || null;

      return {
        status,
        can_submit:
          nested.can_submit ??
          payload.can_submit ??
          !(status === "submitted" || status === "submitting"),
        is_verified:
          nested.is_verified ??
          payload.is_verified ??
          status === "submitted",
        is_retryable: nested.is_retryable ?? payload.is_retryable ?? false,
        submission_id:
          nested.submission_id ??
          payload.submission_id ??
          payload.id ??
          null,
        qr_code: nested.qr_code || payload.qr_code || "",
        ysdc_receipt_number:
          nested.ysdc_receipt_number || payload.ysdc_receipt_number || "",
        error_message: nested.error_message || payload.error_message || null,
      };
    },
    async submit() {
      this.submitting = true;
      try {
        const res = await axios.post(
          `/api/evat/${this.documentType}/${this.documentId}/submit`
        );
        const evat = this.normalizeSubmitResponse(res.data);
        this.$emit("updated", evat);
        if (evat.status !== "failed") {
          this.successMessage = this.isFailed
            ? "Document resent to GRA"
            : "Document sent to GRA";
          this.showSuccess = true;
        }
      } catch (e) {
        // handled by global error interceptor
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.evat-submit-action {
  display: inline-flex;
  align-items: center;
  align-self: center;
  vertical-align: middle;
  gap: 4px;
  height: 36px;
  margin: 0 4px;
}

.evat-submit-action__btn {
  height: 28px !important;
  min-width: auto !important;
  padding: 0 8px !important;
}
</style>
